import React, { useEffect, useState, useMemo } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp, query, where, DocumentData } from "firebase/firestore";
import { db } from "../../firebase";
import { Button, Table, Modal, Group, Loader, Text, Space, TextInput, ThemeIcon, Tooltip, Badge } from "@mantine/core";
import { Plus, Edit, Trash, AlertCircle } from "tabler-icons-react";
import FormSelectOptionsModal from "./form-select-options-modal";
import { FlexFormTemplate, FormFieldConfig, OptionsSourceType } from "@captiva/shared-types";

interface FormSelectOptionsManagerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (docObj: DocumentData) => void;
  selectedField: string | null;
  allTemplates: FlexFormTemplate[];
  editingTemplateId: string;
  companyId: string;
}

export default function FormSelectOptionsManager({
  open,
  onClose,
  onSelect,
  selectedField,
  allTemplates,
  editingTemplateId,
  companyId,
}: FormSelectOptionsManagerProps) {
  const [optionsDocs, setOptionsDocs] = useState<DocumentData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<DocumentData | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState<DocumentData | null>(null);
  const [inUseModalOpen, setInUseModalOpen] = useState<boolean>(false);
  const [inUseDoc, setInUseDoc] = useState<DocumentData | null>(null);
  const [inUseTemplates, setInUseTemplates] = useState<FlexFormTemplate[]>([]);

  useEffect(() => {
    if (!open || !companyId) return;
    setLoading(true);
    const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));

    getDocs(q).then((snapshot) => {
      const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setOptionsDocs(docs.sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "")));
      setLoading(false);
    });
  }, [open, companyId, refresh]);

  const openNew = () => {
    setEditingDoc(null);
    setModalOpen(true);
  };

  const openEdit = (docObj: DocumentData) => {
    setEditingDoc(docObj);
    setModalOpen(true);
  };

  const saveDoc = async (docData: { name: string; options: any[] }) => {
    try {
      const { name, options } = docData;
      if (editingDoc) {
        await updateDoc(doc(db, "flexFormSelectOptions", editingDoc.id), { name, options, companyId, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "flexFormSelectOptions"), { name, options, companyId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      }
      // Refresh
      const q = query(collection(db, "flexFormSelectOptions"), where("companyId", "==", companyId));
      const snapshot = await getDocs(q);
      setOptionsDocs(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setModalOpen(false);
      setRefresh(!refresh);
    } catch (error) {
      console.error("Error saving options document:", error);
    }
  };

  const getUsedTemplates = (docId: string) => {
    return allTemplates.filter((t) => {
      if (t.id === editingTemplateId) return false;
      const fields = t.fields || [];
      return fields.some((f: FormFieldConfig) => {
        const optSrc = f?.optionsSource;
        if (!optSrc || optSrc.type !== OptionsSourceType.FIRESTORE) return false;
        return (
          optSrc.document === docId ||
          optSrc.id === docId ||
          optSrc.optionsSetId === docId
        );
      });
    });
  };

  const onDeleteClick = (docObj: DocumentData) => {
    const used = getUsedTemplates(docObj.id);
    if (used.length > 0) {
      setInUseDoc(docObj);
      setInUseTemplates(used);
      setInUseModalOpen(true);
    } else {
      setPendingDeleteDoc(docObj);
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteDoc) return;
    await deleteDoc(doc(db, "flexFormSelectOptions", pendingDeleteDoc.id));
    setOptionsDocs(optionsDocs.filter((d) => d.id !== pendingDeleteDoc.id));
    setDeleteConfirmOpen(false);
    setPendingDeleteDoc(null);
  };

  if (!open) return null;
  if (loading) return <Loader />;

  return (
    <Modal opened={open} onClose={onClose} title="Manage options sets" size="lg">
      <Group position="apart" mb="md">
        <Text weight={600}>Options sets</Text>
        <Button color="cap-blue" variant="outline" leftIcon={<Plus size={16} />} onClick={openNew}>New options set</Button>
      </Group>
      <TextInput
        placeholder="Search by name or option label..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.currentTarget.value)}
        mb="md"
      />
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {(() => {
          const filteredDocs = optionsDocs.filter((docObj) => {
            if (!searchTerm.trim()) return true;
            const term = searchTerm.toLowerCase();
            const matchesName = (docObj.name || "").toLowerCase().includes(term);
            const matchesOption = docObj.options?.some((opt: any) =>
              (opt.label || "").toLowerCase().includes(term) ||
              String(opt.value ?? "").toLowerCase().includes(term)
            );
            return matchesName || matchesOption;
          });

          if (filteredDocs.length === 0) {
            return (
              <Text size="sm" color="dimmed" align="center" my="md">
                No options sets match your search.
              </Text>
            );
          }

          return (
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Count</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map((docObj) => {
                  const term = searchTerm.trim().toLowerCase();
                  const matchingOptions = term
                    ? (docObj.options || []).filter((opt: any) =>
                        (opt.label || "").toLowerCase().includes(term) ||
                        String(opt.value ?? "").toLowerCase().includes(term)
                      )
                    : [];

                  return (
                    <tr key={docObj.id} style={{ background: selectedField === docObj.id ? '#e0e7ff' : undefined }}>
                      <td>
                        <div>
                          <Text weight={500}>{docObj.name}</Text>
                          {matchingOptions.length > 0 && (
                            <Group spacing={4} mt={2}>
                              <Text size="xs" color="dimmed">Matched option:</Text>
                              {matchingOptions.slice(0, 3).map((opt: any, i: number) => (
                                <Badge key={i} size="xs" variant="outline" color="blue">
                                  {opt.label || opt.value}
                                </Badge>
                              ))}
                              {matchingOptions.length > 3 && (
                                <Text size="xs" color="dimmed">+{matchingOptions.length - 3} more</Text>
                              )}
                            </Group>
                          )}
                        </div>
                      </td>
                      <td style={{ paddingLeft: "2em" }}>{docObj.options?.length || 0}</td>
                    <td>
                      <div style={{ display: "flex", justifyContent: "space-around" }}>
                        <Button size="sm" color="cap-blue" variant="outline" onClick={() => openEdit(docObj)} px={8} style={{ minWidth: 0, marginLeft: 4 }}>
                          <Edit size={16} />
                        </Button>
                        <Tooltip label={getUsedTemplates(docObj.id).length > 0 ? `In use by: ${getUsedTemplates(docObj.id).map((t) => t.name).join(", ")}` : "Delete options set"} withArrow position="top">
                          <Button
                            size="sm"
                            color={getUsedTemplates(docObj.id).length > 0 ? "gray" : "red"}
                            variant="outline"
                            onClick={() => onDeleteClick(docObj)}
                            px={8}
                            style={{ minWidth: 0, marginLeft: 4 }}
                            aria-label={`Delete ${docObj.name}`}
                          >
                            <Trash size={16} />
                          </Button>
                        </Tooltip>
                        <Button size="sm" color="cap-blue" variant="outline" onClick={() => onSelect(docObj)} disabled={selectedField === docObj.id} px={10} style={{ minWidth: 0 }}>
                          Select
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          );
        })()}
      </div>
      <Modal
        opened={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteDoc(null);
        }}
        title="Confirm Delete"
        centered
      >
        <Text mb="md">
          Are you sure you want to delete "{pendingDeleteDoc?.name}"? This action cannot be undone.
        </Text>
        <Group position="right">
          <Button variant="outline" color="gray" onClick={() => {
            setDeleteConfirmOpen(false);
            setPendingDeleteDoc(null);
          }}>
            Cancel
          </Button>
          <Button variant="filled" color="red" onClick={confirmDelete}>
            Delete
          </Button>
        </Group>
      </Modal>
      <Modal
        opened={inUseModalOpen}
        onClose={() => {
          setInUseModalOpen(false);
          setInUseDoc(null);
          setInUseTemplates([]);
        }}
        title={
          <Group spacing="xs">
            <ThemeIcon color="orange" variant="light" size="md">
              <AlertCircle size={18} />
            </ThemeIcon>
            <Text weight={600}>Cannot Delete Option Set</Text>
          </Group>
        }
        centered
      >
        <Text mb="sm" size="sm">
          <strong>"{inUseDoc?.name}"</strong> cannot be deleted because it is currently in use by the following form template{inUseTemplates.length > 1 ? "s" : ""}:
        </Text>
        <div style={{ maxHeight: 180, overflowY: "auto", margin: "0.5em 0 1em" }}>
          <ul style={{ margin: 0, paddingLeft: "1.5em" }}>
            {inUseTemplates.map((t) => (
              <li key={t.id} style={{ marginBottom: "0.25em" }}>
                <Text size="sm" weight={500}>{t.name || "Untitled Form"}</Text>
              </li>
            ))}
          </ul>
        </div>
        <Text size="sm" color="dimmed" mb="lg">
          Please remove or replace this option set in the template(s) listed above before deleting it.
        </Text>
        <Group position="right">
          <Button
            variant="filled"
            color="cap-blue"
            onClick={() => {
              setInUseModalOpen(false);
              setInUseDoc(null);
              setInUseTemplates([]);
            }}
          >
            Understood
          </Button>
        </Group>
      </Modal>
      <FormSelectOptionsModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={saveDoc}
        initialDoc={editingDoc}
      />
      <Space h="xl" />
    </Modal>
  );
}
